/**
 * Parser Data Transformation Utilities
 * Transforms raw parser API response into canonical format
 */

export interface BillHeader {
  invoice_number: string | null;
  invoice_date: string | null;
  billing_period_from: string | null;
  billing_period_to: string | null;
  amount_due: number | null;
  due_date: string | null;
  supplier: string;
  account_number: string | null;
  parsed_at: string;
  confidence_summary: {
    avg: number;
    min: number;
    max: number;
  };
  needs_review: string[];
  raw_payload: Record<string, any>;
  // New fields for meter and identifier data
  meter_data: Record<string, any>;
  identifier_data: Record<string, any>;
  user_defined_fields: Record<string, any>;
  // Additional fields for comprehensive parsing
  customer_name: string | null;
  service_address: string | null;
  previous_balance: number | null;
  payment_received: number | null;
  late_fee: number | null;
  tax_amount: number | null;
  service_charge: number | null;
  discount: number | null;
  usage: number | null;
  rate: number | null;
  meter_number: string | null;
  reading_date: string | null;
  previous_reading: number | null;
  current_reading: number | null;
  consumption: number | null;
}

export interface LineItem {
  file_row_index: number;
  description: string;
  quantity: number | null;
  unit_price: number | null;
  amount: number | null;
  raw: Record<string, any>;
}

export interface TransformResult {
  header: BillHeader;
  lineItems: LineItem[];
}

// Flexible field mapping - supports multiple variations
const FIELD_MAPPING: Record<string, string[]> = {
  'invoice_number': ['InvoiceNumber', 'Invoice Number', 'invoice_num', 'Bill Number', 'BillNumber', 'InvoiceNo', 'InvoiceNo', 'InvoiceID', 'BillID'],
  'invoice_date': ['InvoiceDate', 'Invoice Date', 'Date', 'BillDate', 'Bill Date', 'IssueDate', 'BillingDate'],
  'billing_period_from': ['BillingPeriodFrom', 'Billing Period From', 'PeriodFrom', 'Period From', 'StartDate', 'ServiceFrom', 'FromDate', 'periodFrom'],
  'billing_period_to': ['BillingPeriodTo', 'Billing Period To', 'PeriodTo', 'Period To', 'EndDate', 'ServiceTo', 'ToDate', 'periodTo'],
  'amount_due': ['AmountDue', 'Amount Due', 'TotalAmount', 'Total Amount', 'Total', 'AmountOwed', 'CurrentCharges', 'BalanceDue', 'totalAmountDue', 'currentBillAmount'],
  'due_date': ['DueDate', 'Due Date', 'PaymentDue', 'Payment Due', 'DueDatePayment', 'PaymentDate'],
  'account_number': ['AccountNumber', 'Account Number', 'AccountNo', 'Account', 'AcctNumber', 'CustomerNumber', 'AccountID', 'identifier'],
  'customer_name': ['CustomerName', 'Customer Name', 'Customer', 'AccountHolder', 'BillTo', 'ServiceAddress'],
  'service_address': ['ServiceAddress', 'Service Address', 'Address', 'BillingAddress', 'ServiceLocation', 'siteAddress'],
  'previous_balance': ['PreviousBalance', 'Previous Balance', 'PriorBalance', 'OpeningBalance', 'previousBillAmount'],
  'payment_received': ['PaymentReceived', 'Payment Received', 'Payments', 'Credits', 'previousBillPayment'],
  'late_fee': ['LateFee', 'Late Fee', 'LateCharges', 'Penalty', 'latePaymentFee'],
  'tax_amount': ['TaxAmount', 'Tax Amount', 'Tax', 'GST', 'VAT', 'Sales Tax', 'Gross Receipts Tax', 'State Tax Adjustment'],
  'service_charge': ['ServiceCharge', 'Service Charge', 'ServiceFee', 'DeliveryCharge', 'Customer Charge', 'Distribution Charges'],
  'discount': ['Discount', 'DiscountAmount', 'Rebate', 'Credit', 'adjustments'],
  'usage': ['Usage', 'Consumption', 'KWH', 'Units', 'Quantity', 'serviceUsage', 'usageMonth', 'kH'],
  'rate': ['Rate', 'UnitRate', 'PricePerUnit', 'Tariff', 'rateUOM'],
  'meter_number': ['MeterNumber', 'Meter Number', 'MeterID', 'MeterSerial', 'meterNumber'],
  'reading_date': ['ReadingDate', 'Reading Date', 'MeterReadingDate'],
  'previous_reading': ['PreviousReading', 'Previous Reading', 'LastReading', 'previousReading'],
  'current_reading': ['CurrentReading', 'Current Reading', 'PresentReading', 'currentReading'],
  'consumption': ['Consumption', 'Usage', 'KWH', 'Units', 'Quantity', 'quantity', 'quantityUOM'],
  'supplier_name': ['supplierName', 'SupplierName', 'Supplier Name', 'Utility'],
  'billing_period': ['billingPeriod', 'BillingPeriod', 'Billing Period'],
  'currency': ['currency', 'Currency'],
  'city': ['city', 'City', 'siteCity'],
  'state': ['state', 'State', 'siteState'],
  'postCode': ['postCode', 'PostCode', 'Postal Code', 'sitePostCode'],
  'country': ['country', 'Country', 'siteCountry'],
  'payment_methods': ['paymentMethods', 'PaymentMethods', 'Payment Methods'],
  'annual_usage': ['annualUsage', 'AnnualUsage', 'Annual Usage'],
  'is_consolidate_bill': ['isConsolidateBill', 'IsConsolidateBill', 'Is Consolidate Bill'],
  'is_final_bill': ['isFinalBill', 'IsFinalBill', 'Is Final Bill'],
  'is_ocr_bill': ['isOCRBill', 'IsOCRBill', 'Is OCR Bill'],
  'utility_type': ['utilityType', 'UtilityType', 'Utility Type', 'Service Type'],
  'read_type': ['readType', 'ReadType', 'Read Type', 'Reading Type'],
  'service_total': ['serviceTotal', 'ServiceTotal', 'Service Total', 'Total Service'],
  'line_item_description': ['lineItemDescription', 'LineItemDescription', 'Line Item Description'],
  'meter_multiplier': ['meterMultiplier', 'MeterMultiplier', 'Meter Multiplier'],
  'category': ['category', 'Category'],
  'line_item': ['lineItem', 'LineItem', 'Line Item'],
  'meter_reading_date': ['meterReadingDate', 'Meter Reading Date', 'Reading Date'],
  'sub_account': ['subAccount', 'Sub Account', 'Sub-Account'],
};

/**
 * Find field value using flexible case-insensitive matching
 */
function findFieldValue(results: any, possibleKeys: string[]): any {
  for (const key of possibleKeys) {
    const found = Object.keys(results).find(k => 
      k.toLowerCase().replace(/\s+/g, '') === key.toLowerCase().replace(/\s+/g, '')
    );
    if (found && results[found] !== null && results[found] !== undefined) {
      return results[found];
    }
  }
  return null;
}

/**
 * Normalize date to YYYY-MM-DD format
 */
function normalizeDate(value: any): string | null {
  if (!value) return null;
  
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
}

/**
 * Normalize number to decimal with 2 places
 */
function normalizeNumber(value: any): number | null {
  if (value === null || value === undefined) return null;
  
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value);
  
  if (isNaN(num)) return null;
  
  return Math.round(num * 100) / 100;
}

/**
 * Convert snake_case to Title Case
 */
function toTitleCase(str: string): string {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Extract and normalize meter/identifier data
 */
function extractMeterIdentifierData(data: any, confidence: Record<string, number>, confidenceThreshold: number): {
  meter_data: Record<string, any>;
  identifier_data: Record<string, any>;
} {
  const meter_data: Record<string, any> = {};
  const identifier_data: Record<string, any> = {};

  // Extract meter data
  const meterSource = data.meter || data.meters || {};
  for (const [key, value] of Object.entries(meterSource)) {
    if (value !== null && value !== undefined && value !== '') {
      const normalizedKey = toTitleCase(key);
      const fieldConfidence = confidence[key] || 1;
      
      if (fieldConfidence >= confidenceThreshold) {
        meter_data[normalizedKey] = value;
      }
    }
  }

  // Extract identifier data
  const identifierSource = data.identifier || data.identifiers || {};
  for (const [key, value] of Object.entries(identifierSource)) {
    if (value !== null && value !== undefined && value !== '') {
      const normalizedKey = toTitleCase(key);
      const fieldConfidence = confidence[key] || 1;
      
      if (fieldConfidence >= confidenceThreshold) {
        identifier_data[normalizedKey] = value;
      }
    }
  }

  return { meter_data, identifier_data };
}

/**
 * Calculate confidence summary from confidence object
 */
function calculateConfidenceSummary(confidence: Record<string, number>): {
  avg: number;
  min: number;
  max: number;
} {
  const values = Object.values(confidence).filter(v => typeof v === 'number');
  
  if (values.length === 0) {
    return { avg: 0, min: 0, max: 0 };
  }
  
  const sum = values.reduce((a, b) => a + b, 0);
  const avg = sum / values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  return { avg, min, max };
}

/**
 * Transform raw parser API response into canonical format
 */
export function transformParsed(
  apiResponse: any,
  supplierName: string,
  confidenceThreshold = 0.5
): TransformResult {
  const results = apiResponse?.data?.results || {};
  const confidence = apiResponse?.data?.confidence || {};
  const data = apiResponse?.data || {};
  
  // Extract meter and identifier data
  const { meter_data, identifier_data } = extractMeterIdentifierData(data, confidence, confidenceThreshold);
  
  const header: BillHeader = {
    invoice_number: null,
    invoice_date: null,
    billing_period_from: null,
    billing_period_to: null,
    amount_due: null,
    due_date: null,
    supplier: supplierName,
    account_number: null,
    parsed_at: new Date().toISOString(),
    confidence_summary: calculateConfidenceSummary(confidence),
    needs_review: [],
    raw_payload: {},
    meter_data,
    identifier_data,
    user_defined_fields: {},
    // Initialize additional fields
    customer_name: null,
    service_address: null,
    previous_balance: null,
    payment_received: null,
    late_fee: null,
    tax_amount: null,
    service_charge: null,
    discount: null,
    usage: null,
    rate: null,
    meter_number: null,
    reading_date: null,
    previous_reading: null,
    current_reading: null,
    consumption: null,
  };

  const lineItems: LineItem[] = [];
  
  // Smart field mapping using flexible matching
  for (const [canonicalKey, possibleKeys] of Object.entries(FIELD_MAPPING)) {
    const value = findFieldValue(results, possibleKeys);
    
    if (value !== null) {
      // Find confidence score
      let fieldConfidence = 1;
      for (const key of possibleKeys) {
        const found = Object.keys(confidence).find(k => 
          k.toLowerCase().replace(/\s+/g, '') === key.toLowerCase().replace(/\s+/g, '')
        );
        if (found) {
          fieldConfidence = confidence[found];
          break;
        }
      }
      
      // Check if field is below confidence threshold
      if (fieldConfidence < confidenceThreshold) {
        header.needs_review.push(canonicalKey);
      }
      
      // Map to canonical header fields
      if (canonicalKey === 'invoice_number') {
        header.invoice_number = String(value);
      } else if (canonicalKey === 'invoice_date') {
        header.invoice_date = normalizeDate(value);
      } else if (canonicalKey === 'billing_period_from') {
        header.billing_period_from = normalizeDate(value);
      } else if (canonicalKey === 'billing_period_to') {
        header.billing_period_to = normalizeDate(value);
      } else if (canonicalKey === 'amount_due') {
        header.amount_due = normalizeNumber(value);
      } else if (canonicalKey === 'due_date') {
        header.due_date = normalizeDate(value);
      } else if (canonicalKey === 'account_number') {
        header.account_number = String(value);
      } else if (canonicalKey === 'customer_name') {
        header.customer_name = String(value);
      } else if (canonicalKey === 'service_address') {
        header.service_address = String(value);
      } else if (canonicalKey === 'previous_balance') {
        header.previous_balance = normalizeNumber(value);
      } else if (canonicalKey === 'payment_received') {
        header.payment_received = normalizeNumber(value);
      } else if (canonicalKey === 'late_fee') {
        header.late_fee = normalizeNumber(value);
      } else if (canonicalKey === 'tax_amount') {
        header.tax_amount = normalizeNumber(value);
      } else if (canonicalKey === 'service_charge') {
        header.service_charge = normalizeNumber(value);
      } else if (canonicalKey === 'discount') {
        header.discount = normalizeNumber(value);
      } else if (canonicalKey === 'usage') {
        header.usage = normalizeNumber(value);
      } else if (canonicalKey === 'rate') {
        header.rate = normalizeNumber(value);
      } else if (canonicalKey === 'meter_number') {
        header.meter_number = String(value);
        header.meter_data['Meter Number'] = String(value);
      } else if (canonicalKey === 'reading_date') {
        header.reading_date = normalizeDate(value);
        header.meter_data['Reading Date'] = normalizeDate(value);
      } else if (canonicalKey === 'previous_reading') {
        header.previous_reading = normalizeNumber(value);
        header.meter_data['Previous Reading'] = normalizeNumber(value);
      } else if (canonicalKey === 'current_reading') {
        header.current_reading = normalizeNumber(value);
        header.meter_data['Current Reading'] = normalizeNumber(value);
      } else if (canonicalKey === 'consumption') {
        header.consumption = normalizeNumber(value);
        header.meter_data['Consumption'] = normalizeNumber(value);
      } else if (canonicalKey === 'meter_reading_date') {
        header.reading_date = normalizeDate(value);
        header.meter_data['Reading Date'] = normalizeDate(value);
      } else if (canonicalKey === 'read_type') {
        // Store read type in meter_data
        header.meter_data['Read Type'] = String(value);
      } else if (canonicalKey === 'meter_multiplier') {
        // Store meter multiplier in meter_data
        header.meter_data['Multiplier'] = normalizeNumber(value);
      } else if (canonicalKey === 'utility_type') {
        // Store utility type in identifier_data
        header.identifier_data['Utility Type'] = String(value);
      } else if (canonicalKey === 'sub_account') {
        // Store sub account in identifier_data
        header.identifier_data['Sub Account'] = String(value);
      } else if (canonicalKey === 'service_total') {
        // Store service total in identifier_data
        header.identifier_data['Service Total'] = normalizeNumber(value);
      } else if (canonicalKey === 'identifier') {
        // Store identifier in identifier_data
        header.identifier_data['Identifier'] = String(value);
      } else if (canonicalKey === 'usage_amount') {
        // Store usage amount
        header.usage = normalizeNumber(value);
      } else if (canonicalKey === 'usage_unit') {
        // Store usage unit
        header.raw_payload['usage_unit'] = String(value);
      } else if (canonicalKey === 'billing_period_start') {
        // Store billing period start
        header.billing_period_from = normalizeDate(value);
      } else if (canonicalKey === 'billing_period_end') {
        // Store billing period end
        header.billing_period_to = normalizeDate(value);
      }
    }
  }

  // Handle line items from AI parser
  if (results.line_items && Array.isArray(results.line_items)) {
    results.line_items.forEach((item: any, index: number) => {
      if (item && typeof item === 'object') {
        const lineItem: LineItem = {
          file_row_index: index,
          description: item.description || '',
          amount: item.total || 0,
          quantity: item.quantity || 0,
          unit_price: item.rate || 0,
          raw: item
        };
        lineItems.push(lineItem);
      }
    });
  }
  
  // Store all unmapped fields in raw_payload - COMPREHENSIVE VERSION
  const storeUnmappedFields = (obj: any, prefix: string = '') => {
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      Object.entries(obj).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}_${key}` : key;
        
        // Check if this field is already mapped
        const isMapped = Object.values(FIELD_MAPPING).some(keys => 
          keys.some(k => k.toLowerCase().replace(/\s+/g, '') === key.toLowerCase().replace(/\s+/g, ''))
        );
        
        if (!isMapped && value !== null && value !== undefined) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            // Recursively store nested objects
            storeUnmappedFields(value, fullKey);
          } else {
            // Store the field
            header.raw_payload[fullKey] = value;
          }
        }
      });
    }
  };

  // Store unmapped fields from the main results
  storeUnmappedFields(results);
  
  // Also store unmapped fields from nested data structures
  if (data.meter_data) {
    storeUnmappedFields(data.meter_data, 'meter_data');
  }
  if (data.identifier_data) {
    storeUnmappedFields(data.identifier_data, 'identifier_data');
  }
  if (data.user_defined_fields) {
    storeUnmappedFields(data.user_defined_fields, 'user_defined_fields');
  }

  // Store parser-specific data structures
  if (data.parseridentifiersdata && Array.isArray(data.parseridentifiersdata)) {
    data.parseridentifiersdata.forEach((identifier: any, index: number) => {
      storeUnmappedFields(identifier, `parseridentifiersdata_${index}`);
      
      // Store charges data
      if (identifier.parserchargesdata && Array.isArray(identifier.parserchargesdata)) {
        identifier.parserchargesdata.forEach((charge: any, chargeIndex: number) => {
          storeUnmappedFields(charge, `parseridentifiersdata_${index}_parserchargesdata_${chargeIndex}`);
        });
      }
    });
  }

  if (data.parserconsolidateaccountsdata && Array.isArray(data.parserconsolidateaccountsdata)) {
    data.parserconsolidateaccountsdata.forEach((account: any, index: number) => {
      storeUnmappedFields(account, `parserconsolidateaccountsdata_${index}`);
    });
  }
  
  // Extract line items if present
  const lines = apiResponse?.data?.lines || [];
  lines.forEach((line: any, index: number) => {
    lineItems.push({
      file_row_index: index + 1,
      description: line.description || line.item || '',
      quantity: normalizeNumber(line.quantity || line.qty),
      unit_price: normalizeNumber(line.unit_price || line.price),
      amount: normalizeNumber(line.amount || line.total),
      raw: line,
    });
  });
  
  return { header, lineItems };
}

/**
 * Get confidence level for a field
 */
export function getFieldConfidence(
  apiResponse: any,
  fieldName: string
): number {
  const confidence = apiResponse?.data?.confidence || {};
  
  // Find matching confidence key
  for (const [key, value] of Object.entries(confidence)) {
    if (key.toLowerCase().replace(/\s+/g, '_') === fieldName.toLowerCase()) {
      return typeof value === 'number' ? value : 0;
    }
  }
  
  return 1; // Default to high confidence if not found
}
